import React, { ChangeEvent } from "react";
import styled from "styled-components";

import { FieldOption, resolveOptionLabel } from "./optionLabel";

/**
 * 数値入力フィールド（3桁区切り表示）。
 *
 * - 表示時は `1,234,567` のようにカンマ区切り。
 * - 入力時はカンマや空白を取り除いた数値文字列を `onChange` に渡す。
 * - 数値以外の文字（小数点・マイナスを除く）は無視する。
 * - 末尾の小数点入力中も保持できるように、文字列値で扱う（type="text"）。
 */
interface NumberInputProps {
  label?: string;
  option?: FieldOption | string;
  placeholder?: string;
  /** 値は数値文字列（"1234567" など） */
  value?: string;
  /** カンマ等を取り除いた数値文字列が入る */
  onChange?: (e: { target: { value: string } }) => void;
  /** 末尾に表示する単位ラベル（例: "円"） */
  suffix?: string;
  /** 小数点を許容するか */
  allowDecimal?: boolean;
  width?: string;
  name?: string;
}

const Wrapper = styled.div`
  position: relative;
  width: 100%;
  display: flex;
  align-items: center;
`;

const StyledInput = styled.input<{ $hasSuffix: boolean }>`
  width: 100%;
  text-align: right;

  /* グローバル CSS の input[type="text"]（詳細度 0,1,1）が padding を
   * 12px 4px 12px 12px で上書きするため、&& でクラスを二重にして
   * （0,2,0）勝たせる。これがないとサフィックス（円）と入力値が重なる。 */
  && {
    padding-right: ${({ $hasSuffix }) => ($hasSuffix ? "28px" : undefined)};
  }
`;

const Suffix = styled.span.attrs({ className: "bodytext-13" })`
  position: absolute;
  right: 8px;
  color: var(--soremo-light-gray);
  pointer-events: none;
`;

/**
 * "1234567.5" → "1,234,567.5" 形式で表示する。
 * 不正な値や空文字はそのまま返す。
 */
const formatNumber = (raw: string | undefined): string => {
  if (!raw) return "";
  // 末尾が "." の場合を保つ
  const endsWithDot = raw.endsWith(".");
  const [intPart, decPart] = raw.split(".");
  if (intPart === undefined) return raw;
  const n = Number.parseFloat(intPart);
  const intFormatted = Number.isNaN(n)
    ? intPart
    : n.toLocaleString("ja-JP", { maximumFractionDigits: 0 });
  if (decPart !== undefined) return `${intFormatted}.${decPart}`;
  if (endsWithDot) return `${intFormatted}.`;
  return intFormatted;
};

const NumberInput: React.FC<NumberInputProps> = ({
  label,
  option,
  placeholder,
  value,
  onChange,
  suffix,
  allowDecimal = false,
  width = "100%",
  name,
}) => {
  const { text: optionLabel, className: optionClass } = resolveOptionLabel(option);
  const containerClass =
    width === "100%" ? "u-col u-w100 u-gap4" : "u-col u-fill u-gap4";

  const handleChange = (e: ChangeEvent<HTMLInputElement>) => {
    const input = e.target.value;
    // カンマ・空白を除去
    let cleaned = input.replace(/[,\s]/g, "");
    if (allowDecimal) {
      // 数字と1個までの小数点のみ
      cleaned = cleaned.replace(/[^\d.]/g, "");
      const firstDot = cleaned.indexOf(".");
      if (firstDot !== -1) {
        cleaned =
          cleaned.slice(0, firstDot + 1) +
          cleaned.slice(firstDot + 1).replace(/\./g, "");
      }
    } else {
      cleaned = cleaned.replace(/\D/g, "");
    }
    onChange?.({ target: { value: cleaned } });
  };

  return (
    <div className={containerClass}>
      {label && (
        <label className="bodytext-13 u-line-height-100 u-pl4">
          {label}
          {optionLabel && <span className={optionClass}>{optionLabel}</span>}
        </label>
      )}
      <Wrapper>
        <StyledInput
          type="text"
          inputMode={allowDecimal ? "decimal" : "numeric"}
          placeholder={placeholder}
          name={name}
          value={formatNumber(value)}
          onChange={handleChange}
          $hasSuffix={!!suffix}
        />
        {suffix && <Suffix>{suffix}</Suffix>}
      </Wrapper>
    </div>
  );
};

export default NumberInput;
